<template>
  <AppDialog
    class="insights-smart-notifications-dialog"
    append-to-body
    is-dismissable
    :show="show"
    :title="rule.name"
    @close="close"
  >
    <section class="header-description">
      <div>
        <i class="hi hi-bell mr-5 align-baseline"></i>
        {{ createdAt }}
      </div>
      <div class="text-capitalize mt-10">
        <i class="hi hi-info mr-5 align-baseline"></i>
        {{ rule.frequency }}
        {{ ruleSubtitle }}
        <span v-if="rule.metric_type === 'time_spent_in_tool'">
          in 1 or more of the following {{ computedAppURLCopy }}:
        </span>
        <ul>
          <li
            v-for="(tool, index) in headTools"
            :key="index"
          >
            {{ tool }}
          </li>
        </ul>
      </div>
      <span
        v-if="restTools.length"
        v-tooltip="tooltipOptions"
        :title="tooltipContent"
        class="remaining-tools"
      >
        +{{ restTools.length }} {{ computedAppURLCopy }}
      </span>
      <div
        v-if="ruleHasDataPrivacy"
        class="mt-10"
      >
        *Excluding users with Data Privacy setting enabled
      </div>
    </section>
    <div class="table-wrapper">
      <AppTable
        :table-data="tableData"
        @sort="sort"
      >
        <AppTableColumn
          v-slot="{ key }"
          cell-classes="text-right p-0"
        >
          {{ key + 1 }}.
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          header="Member"
          name="name"
          cell-classes="w-full"
          :sort-by="buildSortOptions('name')"
        >
          <section class="member">
            <AppAvatar
              class="ml-5"
              :avatar="row.avatar"
              size="speck"
            />
            <div class="member-info">
              <div class="d-flex justify-space-between">
                <span>{{ row.name }}</span>
              </div>
            </div>
          </section>
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          header="Trigger value"
          name="value"
          header-classes="text-right"
          :sort-by="buildSortOptions('value')"
        >
          <div class="text-right">
            {{ getConditionValue(row.value).value }} {{ getConditionValue(row.value).addon }}
          </div>
        </AppTableColumn>
        <AppTableColumn v-slot="{ row }">
          <InsightsViewFullDayLink
            v-if="showFullDayLink(rule.frequency)"
            :date="notification.from"
            :organization-id="organization.id"
            :user-id="row.user_id"
          />
        </AppTableColumn>
        <div
          v-if="notification.matches.length >= notification.limit"
          class="header-description mt-10"
        >
          *Smart notifications are limited to {{ notification.limit }} members per alert, consider breaking this
          notification up into smaller groups.
        </div>
      </AppTable>
    </div>
  </AppDialog>
</template>

<script>
import dayjs from 'dayjs';
import { formatDate } from '~/utils/time_utils';
import { SETTINGS_SMART_NOTIFICATIONS } from '~/app_constants';
import { orderBy } from 'lodash-es';
import { mapState } from 'vuex';
import InsightsViewFullDayLink from '~/components/insights/InsightsViewFullDayLink.vue';
import SmartNotificationMixin from '~/mixins/Insights/SmartNotificationMixin';

const APPS_PER_CARD = 5;

export default {
  name: 'InsightsSmartNotificationsDialog',
  components: { InsightsViewFullDayLink },
  mixins: [SmartNotificationMixin],
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    notification: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    loading: false,
    tooltipOptions: Object.freeze({ placement: 'top', html: true }),
    APPS_PER_CARD,
    sortOptions: {
      field: 'name',
      direction: 'asc',
    },
  }),
  computed: {
    ...mapState(['organization']),
    tableData() {
      return orderBy(this.notification.matches, this.sortOptions.field, this.sortOptions.direction);
    },
    rule() {
      return this.notification.snapshot;
    },
    ruleHasDataPrivacy() {
      const privacyMetrics = ['time_spent_in_tool'];

      return privacyMetrics.includes(this.rule.metric_type);
    },
    tooltipContent() {
      const tools = this.restTools.map((tool) =>
       `<li class="no-wrap">${tool}</li>`
      ).join('');

      return `<ul class="text-left p-20">${tools}</ul>`;
    },
    computedMetricType() {
      const metricTypeDict = {
        application: 'time_spent_in_app',
        url: 'time_spent_in_url',
      };

      if (this.rule.metric_type !== 'time_spent_in_tool') {
        return SETTINGS_SMART_NOTIFICATIONS.insights.metricType[this.rule.frequency][this.rule.metric_type];
      }

      if (this.rule.data && this.rule.data.tools) {
        return SETTINGS_SMART_NOTIFICATIONS.insights.metricType[this.rule.frequency][
          metricTypeDict[this.rule.data.tools.type]
        ];
      }

      return '';
    },
    computedAppURLCopy() {
      return this.rule.data.tools.type === 'application' ? 'Apps' : 'URLs';
    },
    hasTools() {
      return !!this.rule?.data?.tools;
    },
    createdAt() {
      return formatDate(dayjs(this.notification.created_at));
    },
    tools() {
      if (this.hasTools) {
        const tools = Object.values(this.rule.data.tools.ids) || [];
        const names = this.rule.data.tools.names;

        return [...tools, ...names]
      }

      return [];
    },
    headTools() {
      return this.tools.slice(0, APPS_PER_CARD)
    },
    restTools() {
      return this.tools.slice(APPS_PER_CARD)
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    sort({ field, direction }) {
      this.sortOptions = { field, direction };
    },
    buildSortOptions(field) {
      return {
        field,
        direction: this.sortOptions.field === field ? this.sortOptions.direction : null,
      };
    },
    showFullDayLink(frequency) {
      return ['hourly', 'daily'].includes(frequency);
    },
  },
};
</script>
